import { useCallback, useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { Save } from 'lucide-react'
import { getJsonRaw, saveJson } from '@/api/generateapp'
import { useProjectStore } from '@/store/projectStore'
import { refreshJourney } from '@/store/journeyStore'
import { useProjectPermissions } from '@/auth/useProjectPermissions'
import { ReadOnlyBadge } from '@/auth/ReadOnlyBadge'
import type { ProjectAuthConfig } from '@twasta/page-schema'
import type { RawAppData } from '@/types'
import { AuthConfigEditor, defaultAuthConfig } from '../AuthConfigEditor'

/**
 * Sign-in and rights rules: the SAME AuthConfigEditor the old Application
 * Properties carried (end-user authentication + per-object/action rights),
 * on its own. Mounted at Advanced › Sign-in and rights rules and in the
 * People tab's "How people sign in" drawer.
 */
export default function SignInRulesPanel({ onSaved }: { onSaved?: () => void }) {
  const { currentProject } = useProjectStore()
  const perms = useProjectPermissions()
  const folder = currentProject?.application.folder_name || ''
  const [appData, setAppData] = useState<RawAppData | null>(null)
  const [auth, setAuth] = useState<ProjectAuthConfig>(defaultAuthConfig())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    if (!folder) return
    try {
      const data = (await getJsonRaw(folder)) as RawAppData
      setAppData(data)
      setAuth((data.application.auth as ProjectAuthConfig | undefined) ?? defaultAuthConfig())
      setError(null)
    } catch (err) {
      console.error('[signin-rules] could not load application.json', err)
      setError('The sign-in rules could not be loaded.')
    }
  }, [folder])

  useEffect(() => { void load() }, [load])

  const save = async () => {
    if (!appData) return
    setSaving(true)
    try {
      // Re-read first so a concurrent edit of other settings is not overwritten.
      const fresh = (await getJsonRaw(folder)) as RawAppData
      const updated = { ...fresh, application: { ...fresh.application, auth } }
      await saveJson(folder, updated)
      setAppData(updated)
      toast.success('Sign-in rules saved')
      refreshJourney()
      onSaved?.()
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      toast.error(typeof detail === 'string' && detail ? detail : 'Failed to save the sign-in rules')
    } finally {
      setSaving(false)
    }
  }

  if (error) return <p style={{ color: 'var(--color-error)' }}>{error}</p>
  if (!appData) return <div className="spinner" style={{ margin: '2rem auto' }} />
  const canEdit = !perms.loaded || perms.canUpdateProperties
  const nav = appData.navigation || []

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <p style={{ margin: 0, color: 'var(--color-text-secondary)' }}>
        End-user sign-in and the per-screen, per-action rights of the app. They ship with the app and
        are enforced wherever it runs.
      </p>
      <AuthConfigEditor
        value={auth}
        onChange={setAuth}
        objectNames={nav.filter((n) => (n.obj_type === 'T' || n.obj_type === 'D') && !!n.obj_name).map((n) => n.obj_name)}
        pageNames={nav.filter((n) => n.obj_type === 'S' && !!n.obj_name).map((n) => n.obj_name)}
      />
      <div>
        {canEdit ? (
          <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save sign-in rules'}
          </button>
        ) : <ReadOnlyBadge />}
      </div>
    </div>
  )
}
